import React from 'react';
import { AlertCircle, Truck, User } from 'lucide-react';
import { useTranslation } from '../../i18n';
import MarkdownLite from '../MarkdownLite';

/**
 * AssignmentConflictModal — secondary confirmation that lists which active
 * contracts would be lost if the player force-reassigns the selected
 * vehicle / driver pair. Extracted from `ContractAssignModal.jsx`
 * (04/06/2026 refactor). Pure presentation, zero behaviour change.
 */
export default function AssignmentConflictModal({ conflict, onCancel, onForce }) {
  const { t } = useTranslation();
  if (!conflict) return null;
  const showDriver = conflict.driverConflict
    && conflict.driverConflict.instanceId !== conflict.vehicleConflict?.instanceId;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="assignment-conflict-overlay"
      onClick={onCancel}
    >
      <div
        className="bg-ink-800 rounded-xl w-full max-w-lg shadow-2xl border border-rose-500/40 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="assignment-conflict-modal"
      >
        <div className="bg-rose-600 px-5 py-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-white" />
          <h3 className="text-white font-bold text-base">{t('assignment_conflict_title')}</h3>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-200">
            <MarkdownLite as="span" text={t('assignment_conflict_intro')} />
          </p>
          <div className="space-y-2">
            {conflict.vehicleConflict && (
              <ConflictRow
                kind="vehicle"
                icon={<Truck className="w-3.5 h-3.5" />}
                label={t('assignment_conflict_vehicle_busy')}
                payload={conflict.vehicleConflict}
                tDays={t}
              />
            )}
            {showDriver && (
              <ConflictRow
                kind="driver"
                icon={<User className="w-3.5 h-3.5" />}
                label={t('assignment_conflict_driver_busy')}
                payload={conflict.driverConflict}
                tDays={t}
              />
            )}
          </div>
          <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
            ⚠️ {t('assignment_conflict_warning')}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="text-xs px-3 py-2 rounded-md font-bold bg-ink-700 text-slate-200 hover:bg-ink-600 transition-colors"
            data-testid="conflict-cancel-btn"
          >
            {t('assignment_conflict_keep')}
          </button>
          <button
            onClick={onForce}
            className="text-xs px-4 py-2 rounded-md font-bold bg-rose-600 text-white hover:bg-rose-500 transition-colors"
            data-testid="conflict-force-btn"
          >
            {t('assignment_conflict_force')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConflictRow({ kind, icon, label, payload, tDays }) {
  return (
    <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs" data-testid={`conflict-${kind}`}>
      <p className="font-bold text-rose-200 flex items-center gap-1.5">
        {icon} {label}
      </p>
      <p className="text-slate-200 mt-0.5">
        « {payload.name} »
        {payload.clientName && <span className="text-slate-400"> — {payload.clientName}</span>}
        {Number.isFinite(payload.daysRemaining) && (
          <span className="text-slate-500"> · {tDays('assignment_conflict_days_remaining', { days: payload.daysRemaining })}</span>
        )}
      </p>
    </div>
  );
}
